import { useState } from "react";
import { ALL_GENRES, ALL_YEARS, SORT_OPTIONS, type SortKey } from "@/lib/catalog";

export interface FilterState {
  genres: string[];
  year: string;
  minRating: number;
  sort: SortKey;
}

interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  resultCount: number;
}

const RATING_OPTIONS = [
  { value: 0, label: "全部评分" },
  { value: 7, label: "7 分以上" },
  { value: 8, label: "8 分以上" },
  { value: 8.5, label: "8.5 分以上" },
];

export default function FilterBar({
  value,
  onChange,
  onReset,
  resultCount,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const toggleGenre = (genre: string) => {
    const next = value.genres.includes(genre)
      ? value.genres.filter((g) => g !== genre)
      : [...value.genres, genre];
    onChange({ ...value, genres: next });
  };

  const selectClass =
    "h-9 px-3 pr-8 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-800 outline-none cursor-pointer focus:border-primary-300";

  return (
    <div className="rounded-lg bg-background-100 border border-background-200 p-4">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-9 px-4 inline-flex items-center gap-1.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选条件
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>
        <span className="text-xs text-foreground-500">
          共 <strong className="text-primary-600">{resultCount}</strong> 条
        </span>
      </div>

      <div className={`${open ? "block" : "hidden"} md:block mt-4 md:mt-0`}>
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span className="text-sm text-foreground-500 whitespace-nowrap pt-1.5 w-12 shrink-0">
              类型
            </span>
            <div className="flex flex-wrap gap-2">
              {ALL_GENRES.map((genre) => {
                const active = value.genres.includes(genre);
                return (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => toggleGenre(genre)}
                    className={`px-3 h-8 text-sm rounded-full border transition-colors cursor-pointer ${
                      active
                        ? "bg-primary-500 text-background-50 border-primary-500"
                        : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {genre}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">年份</span>
              <select
                value={value.year}
                onChange={(e) => onChange({ ...value, year: e.target.value })}
                className={selectClass}
                aria-label="按年份筛选"
              >
                <option value="all">全部年份</option>
                {ALL_YEARS.map((y) => (
                  <option key={y} value={String(y)}>
                    {y} 年
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">评分</span>
              <select
                value={value.minRating}
                onChange={(e) =>
                  onChange({ ...value, minRating: Number(e.target.value) })
                }
                className={selectClass}
                aria-label="按评分筛选"
              >
                {RATING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={onReset}
              className="h-9 px-3 inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap items-center justify-between gap-3">
          <span className="hidden md:inline text-sm text-foreground-500">
            共 <strong className="text-primary-600">{resultCount}</strong> 条结果
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-500">排序</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...value, sort: opt.value })}
                className={`px-3 h-8 text-sm rounded-md transition-colors cursor-pointer ${
                  value.sort === opt.value
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-50 text-foreground-700 border border-background-200 hover:text-secondary-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}